import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { BookOpenText, RefreshCw, Share2, X } from 'lucide-react';
import type { Ayah } from '@/types/quran';
import {
  getRandomVerse,
  getTranslations,
  getAyahWithTranslations,
  getSurahs,
} from '@/services/quran.service';
import {
  getTodayOneDayOneAyah,
  markTodayDismissed,
  saveTodayOneDayOneAyah,
  type OneDayLanguage,
} from '@/services/oneDayOneAyah.service';
import { removeAyahNumber } from '@/components/share/OneDayOneAyahSharePage';

interface OneDayOneAyahDialogProps {
  open: boolean;
  onClose: () => void;

  onShareClick?: (state: {
    ayah: Ayah;
    meaningLanguage: OneDayLanguage;
    showArabic: boolean;
  }) => void;
}

const DEFAULT_THAI_TRANSLATION_ID = 51; // default Thai translation used in ReadPage (Tadabbur QF)

let cachedEnglishTranslationId: number | null | undefined;

async function getDefaultEnglishTranslationId(): Promise<number | null> {
  if (cachedEnglishTranslationId !== undefined) {
    return cachedEnglishTranslationId;
  }
  try {
    const all = await getTranslations();
    const english = all.find(
      (t) => t.languageName?.toLowerCase() === 'english',
    );
    cachedEnglishTranslationId = english?.id ?? null;
    return cachedEnglishTranslationId;
  } catch (error) {
    console.error('[OneDayOneAyah] Failed to load English translation id', error);
    cachedEnglishTranslationId = null;
    return null;
  }
}

export function OneDayOneAyahDialog({
  open,
  onClose,
  onShareClick,
}: OneDayOneAyahDialogProps) {
  const [ayah, setAyah] = useState<Ayah | null>(null);
  const [surahName, setSurahName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [meaningLanguage, setMeaningLanguage] = useState<OneDayLanguage>('th');
  const [showArabic, setShowArabic] = useState<boolean>(true);

  const loadRandomAyah = useCallback(
    async (lang: OneDayLanguage) => {
      setLoading(true);
      try {
        let translationIds: number[] | undefined;
        if (lang === 'th') {
          translationIds = [DEFAULT_THAI_TRANSLATION_ID];
        } else {
          const englishId = await getDefaultEnglishTranslationId();
          translationIds = englishId ? [englishId] : undefined;
        }
        const verse = await getRandomVerse(translationIds);

        setAyah(verse);
        if (verse) {
          const surahNumber = verse.surahNumber ?? 0;
          if (surahNumber > 0) {
            const allSurahs = await getSurahs();
            const foundSurah = allSurahs.find((s) => s.number === surahNumber);
            if (foundSurah) {
              setSurahName(foundSurah.name);
            }
          }

          saveTodayOneDayOneAyah({
            ayah: verse,
            meaningLanguage: lang,
            showArabic,
          });
        }
      } catch (error) {
        console.error('[OneDayOneAyah] Failed to load random ayah', error);
        setAyah(null);
        setSurahName('');
      } finally {
        setLoading(false);
      }
    },
    [showArabic],
  );

  const hydrateFromStorage = useCallback(() => {
    const stored = getTodayOneDayOneAyah();
    if (stored) {
      setAyah(stored.ayah);
      setMeaningLanguage(stored.meaningLanguage);
      setShowArabic(stored.showArabic);

      // Hydrate surah name
      if (stored.ayah && stored.ayah.surahNumber) {
        getSurahs().then((surahs) => {
          const s = surahs.find(s => s.number === stored.ayah.surahNumber);
          if (s) setSurahName(s.name);
        });
      }

      return true;
    }
    return false;
  }, []);

  const updateCurrentAyahTranslation = useCallback(
    async (targetLanguage: OneDayLanguage) => {
      if (!ayah) {
        await loadRandomAyah(targetLanguage);
        return;
      }

      const surahNumber = ayah.surahNumber ?? 0;
      if (!surahNumber) {
        await loadRandomAyah(targetLanguage);
        return;
      }

      setLoading(true);
      try {
        let updatedAyah: Ayah = ayah;

        let translationIds: number[] | undefined;
        if (targetLanguage === 'th') {
          translationIds = [DEFAULT_THAI_TRANSLATION_ID];
        } else {
          const englishId = await getDefaultEnglishTranslationId();
          translationIds = englishId ? [englishId] : undefined;
        }

        if (translationIds && translationIds.length > 0) {
          updatedAyah = await getAyahWithTranslations(
            surahNumber,
            ayah.numberInSurah,
            translationIds,
          );
        } else {
          updatedAyah = { ...ayah, translations: [] };
        }

        setAyah(updatedAyah);
        saveTodayOneDayOneAyah({
          ayah: updatedAyah,
          meaningLanguage: targetLanguage,
          showArabic,
        });
      } catch (error) {
        console.error('[OneDayOneAyah] Failed to update translation', error);
      } finally {
        setLoading(false);
      }
    },
    [ayah, loadRandomAyah, showArabic],
  );

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      const hadStored = hydrateFromStorage();
      if (!hadStored && !ayah) {
        void loadRandomAyah(meaningLanguage);
      }
    } else {
      onClose();
    }
  };

  const handleLanguageChange = (nextLanguage: OneDayLanguage) => {
    if (nextLanguage === meaningLanguage) return;
    setMeaningLanguage(nextLanguage);
    void updateCurrentAyahTranslation(nextLanguage);
  };

  const handleToggleArabic = (value: boolean) => {
    setShowArabic(value);
    if (ayah) {
      saveTodayOneDayOneAyah({
        ayah,
        meaningLanguage,
        showArabic: value,
      });
    }
  };

  const handleRandomClick = () => {
    void loadRandomAyah(meaningLanguage);
  };

  const handleDismissToday = () => {
    markTodayDismissed();
    onClose();
  };

  const handleShare = () => {
    if (ayah && onShareClick) {
      onShareClick({
        ayah,
        meaningLanguage,
        showArabic,
      });
    }
  };

  useEffect(() => {
    if (open) {
      const hadStored = hydrateFromStorage();
      if (!hadStored && !ayah) {
        void loadRandomAyah(meaningLanguage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay onClick={handleDismissToday} />
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-md w-[calc(100%-2rem)] rounded-2xl p-6">
          {/* Close button */}
          <button
            type="button"
            onClick={handleDismissToday}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>

          <DialogHeader className="flex flex-row items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <BookOpenText className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-base">One Day One Ayah</DialogTitle>
              <DialogDescription className="text-xs">
                อายะฮ์สำหรับวันนี้ — สุ่มจากอัลกุรอาน
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {/* Ayah content */}
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : ayah ? (
              <div className="space-y-3">
                {showArabic && (
                  <div
                    className="p-4 rounded-xl bg-muted/50 font-amiri-quran text-2xl text-right leading-loose"
                    dir="rtl"
                  >
                    {ayah.text ? (
                      <span dangerouslySetInnerHTML={{ __html: removeAyahNumber(ayah.text) }} />
                    ) : (
                      ayah.text
                    )}
                  </div>
                )}

                {ayah.translations && ayah.translations.length > 0 && (
                  <div className="p-3 rounded-lg border bg-card text-sm leading-relaxed">
                    {ayah.translations[0].text}
                    {ayah.translations[0].resourceName && (
                      <p className="text-xs text-muted-foreground mt-2">
                        — {ayah.translations[0].resourceName}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  {surahName ? `ซูเราะฮ์ ${surahName} (${ayah.surahNumber})` : `ซูเราะฮ์ที่ ${ayah.surahNumber}`} อายะฮ์ที่ {ayah.numberInSurah} · หน้า {ayah.page}{' '}
                  · ญุซอ์ {ayah.juz}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                ไม่สามารถโหลดอายะฮ์ได้ ลองสุ่มใหม่อีกครั้ง
              </p>
            )}

            {/* Source & language controls */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">ภาษาคำแปล (Quran Foundation)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleLanguageChange('th')}
                    className={cn(
                      'flex flex-col items-start gap-0.5 p-3 rounded-lg border text-left text-xs transition-colors',
                      meaningLanguage === 'th'
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-primary/50',
                    )}
                  >
                    <span className="font-medium">ภาษาไทย</span>
                    <span className="text-[11px] text-muted-foreground">
                      แปลภาษาไทย
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLanguageChange('en')}
                    className={cn(
                      'flex flex-col items-start gap-0.5 p-3 rounded-lg border text-left text-xs transition-colors',
                      meaningLanguage === 'en'
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-primary/50',
                    )}
                  >
                    <span className="font-medium">English</span>
                    <span className="text-[11px] text-muted-foreground">
                      English Translation
                    </span>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="one-day-show-arabic"
                    checked={showArabic}
                    onCheckedChange={handleToggleArabic}
                  />
                  <Label
                    htmlFor="one-day-show-arabic"
                    className="text-xs font-normal"
                  >
                    แสดงอายะฮ์อาหรับ
                  </Label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2 pt-1">
              <Button
                onClick={handleRandomClick}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                สุ่มอายะฮ์ใหม่
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleDismissToday}
                  variant="ghost"
                  className="flex-1 text-xs"
                >
                  ปิดการแสดงวันนี้
                </Button>
                <Button
                  type="button"
                  onClick={handleShare}
                  className="flex-1 text-xs"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  แชร์
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
